import React, { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import CentralizedSpinner from 'components/CentralizedSpinner';
import TabbedPage from 'components/TabbedPage';
import RightSidebar from 'components/Layout/RightSidebar';
import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import SectionTabs, { SectionTabItem } from 'components/Layout/MobileShell/SectionTabs';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import DecompControlPanel from 'components/Relatorios/Decomp/ControlPanel';
import DessemControlPanel from 'components/Relatorios/Dessem/ControlPanel';
import DessemConsultaCard from 'components/Relatorios/Dessem/Mobile/ConsultaCard';
import IntersemanalControlPanel from 'components/Relatorios/Intersemanal/ControlPanel';
import MatrizControlPanel from 'components/Relatorios/Matriz/ControlPanel';
import SemanalControlPanel from 'components/Relatorios/Semanal/ControlPanel';
import PainelSheetGenerico from 'components/Relatorios/shared/Mobile/PainelSheetGenerico';
import MensalControlPanel from 'components/Relatorios/Mensal/ControlPanel';
import useDessemHidratacao from 'hooks/Relatorios/Dessem/useDessemHidratacao';
import { openRight } from 'redux/reducers/sidebarsReducer';
import shortenedTabPath from 'helper/shortenedTabPath';
import usePermissionCheck from 'utils/hooks/usePermissionCheck';
import useIsMobile from 'utils/hooks/useIsMobile';

import './index.scss';

type RelatoriosGroup = 'diario' | 'intersemanal' | 'semanal' | 'mensal';

interface RelatoriosTab {
  title: string;
  path: string;
  showControlPanel?: boolean;
}

const GROUP_TITLES: Record<RelatoriosGroup, string> = {
  diario: 'Informe Diário',
  intersemanal: 'Informe Intersemanal',
  semanal: 'Informe Semanal',
  mensal: 'Informe Mensal',
};

const GROUP_TABS: Record<RelatoriosGroup, RelatoriosTab[]> = {
  diario: [
    { title: 'Informe DECOMP', path: '/relatorios/decomp/', showControlPanel: true },
    { title: 'Informe DESSEM', path: '/relatorios/dessem/', showControlPanel: true },
    { title: 'Comparador', path: '/relatorios/comparador' },
  ],
  intersemanal: [
    { title: 'Informe Intersemanal', path: '/relatorios/intersemanal/', showControlPanel: true },
    { title: 'Comparador', path: '/relatorios/intersemanal/comparador' },
  ],
  semanal: [
    { title: 'Informe Semanal', path: '/relatorios/semanal/', showControlPanel: true },
    { title: 'Matriz', path: '/relatorios/semanal/matriz', showControlPanel: true },
    { title: 'Comparador', path: '/relatorios/semanal/comparador' },
  ],
  mensal: [
    { title: 'Informe Mensal', path: '/relatorios/mensal/', showControlPanel: true },
    { title: 'Comparador', path: '/relatorios/mensal/comparador' },
  ],
};

function getGroup(pathname: string): RelatoriosGroup {
  if (pathname.startsWith('/relatorios/intersemanal')) return 'intersemanal';
  if (pathname.startsWith('/relatorios/semanal')) return 'semanal';
  if (pathname.startsWith('/relatorios/mensal')) return 'mensal';
  return 'diario';
}

/**
 * Layout do módulo Relatórios (espelha WeatherLayout): título + TabbedPage com as
 * abas do informe ativo (route-based) + RightSidebar com o painel de controle,
 * exibido só nas abas que declaram `showControlPanel`. Cada card da home de
 * Relatórios (Diário, Intersemanal, Semanal, Mensal) tem seu próprio grupo de
 * abas e título — derivados da URL, não de estado Redux.
 *
 * Como os outros módulos (ver `RouteWrappers/Windx`), obtém a `product_key` de
 * `relatorios` antes de montar qualquer tela: os endpoints mandam a chave em
 * `params`, fora do alcance da recuperação pós-403 do baseQuery, então sem este
 * gate a primeira consulta sairia com a sentinela `__missing__relatorios` e o
 * backend real responderia 403 "Chave do Produto Inválida" (com o mock isso não
 * aparecia porque o nginx só validava o JWT).
 *
 * Nas rotas do DESSEM há um segundo gate, depois da chave: a data/cenário iniciais vêm
 * do `GET options` (`useDessemHidratacao`, uma vez por sessão) e, até chegarem, nenhum
 * filho monta — as seções (Outlet) e o `DessemControlPanel` consultariam o backend com
 * a data provisória, e o `GET aprovacao` de admin criaria o rascunho do dia errado.
 * As outras rotas não passam por esse gate.
 */
export default function RelatoriosLayout() {
  const keySaved = usePermissionCheck('relatorios');
  const [showSidebar, setShowSidebar] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const group = getGroup(location.pathname);
  const isDessem = location.pathname.startsWith('/relatorios/dessem');
  const isMatriz = location.pathname.startsWith('/relatorios/semanal/matriz');
  // Só com a chave salva (o options manda `product_key` em `params`) e só no DESSEM.
  const dessemHidratado = useDessemHidratacao(keySaved && isDessem);

  let controlPanel = <DecompControlPanel />;
  if (group === 'intersemanal') {
    controlPanel = <IntersemanalControlPanel />;
  } else if (isMatriz) {
    controlPanel = <MatrizControlPanel />;
  } else if (group === 'semanal') {
    controlPanel = <SemanalControlPanel />;
  } else if (group === 'mensal') {
    controlPanel = <MensalControlPanel />;
  } else if (isDessem) {
    controlPanel = <DessemControlPanel />;
  }

  useEffect(() => {
    // No mobile não há RightSidebar: o painel vive num BottomSheet.
    if (!isMobile) dispatch(openRight());
  }, [dispatch, isMobile]);

  if (!keySaved || (isDessem && !dessemHidratado)) {
    return <CentralizedSpinner />;
  }

  if (isMobile) {
    // O `path` das pílulas é o mesmo que `shortenedTabPath` devolve para as rotas do grupo.
    const tabs: SectionTabItem[] = GROUP_TABS[group].map(({ title, path }) => ({ title, path }));
    const ativa = GROUP_TABS[group].find((tab) => tab.path === shortenedTabPath(location.pathname));
    // O DESSEM tem sheet próprio; os demais reaproveitam o painel do desktop num BottomSheet.
    let painelMobile: React.ReactNode = null;
    if (ativa?.showControlPanel) {
      painelMobile = isDessem
        ? <DessemConsultaCard />
        : <PainelSheetGenerico>{controlPanel}</PainelSheetGenerico>;
    }
    // Só a Matriz é showControlPanel sem `RelatorioSecoesView`.
    const painelViaSecoes = !isMatriz;

    return (
      <div className="relatorios-mobile">
        <MobileSectionHeader
          backLabel={GROUP_TITLES[group]}
          title={ativa?.title ?? ''}
          onBack={() => navigate('/relatorios')}
          onAvatarClick={() => setMoreOpen(true)}
        />
        <SectionTabs tabs={tabs} sheetTitle={GROUP_TITLES[group]} />
        {/* Ordem mobile: cabeçalho, N1, N2, "Consulta atual", conteúdo. As rotas de informe
            (RelatorioSecoesView, dona do N2) recebem o painel por contexto e o renderizam logo
            abaixo do N2. As que não passam por ela (Matriz) mantêm o painel aqui, abaixo do N1.
            O Comparador não tem painel. */}
        {painelMobile && !painelViaSecoes && painelMobile}
        <Outlet context={painelViaSecoes ? { painelMobile } : undefined} />
        <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
      </div>
    );
  }

  return (
    <>
      <Row>
        <Col className="md-6">
          <h1 className="mb-3">{GROUP_TITLES[group]}</h1>
        </Col>
      </Row>
      <Row className="flex-md-nowrap" style={{ paddingBottom: '3.3125rem' }}>
        <Col xs={12} md="auto" className="flex-fill overflow-hidden">
          <TabbedPage setControlPanelVisibility={setShowSidebar}>
            {GROUP_TABS[group].map((tab) => (
              <TabbedPage.Tab
                key={tab.path}
                title={tab.title}
                path={tab.path}
                showControlPanel={tab.showControlPanel}
              />
            ))}
          </TabbedPage>
        </Col>
        {showSidebar && (
          <Col xs={12} md="auto">
            <RightSidebar>
              {controlPanel}
            </RightSidebar>
          </Col>
        )}
      </Row>
      <div className="bottom-bar" />
    </>
  );
}
